import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { BackButton } from '../components/BackButton.js';
import { PageTitle } from '../components/Layouts.js';
import { useLocale } from '../components/site.js';
import { LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { OrderForm } from './Checkout.js';

/**
 * Order a fixed-price service and pay it now (T-71): the same address, VAT and payment choice as
 * a plan; services with a "from" or hourly price stay a request for a quote.
 */
export function ServiceOrder() {
  const { serviceCode = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const siteId = params.get('site') ?? undefined;
  const findingId = params.get('finding') ?? undefined;
  const services = useQuery({
    queryKey: ['services', locale],
    queryFn: () => unwrap(api.GET('/services', { params: { query: { locale } } })),
    staleTime: 60_000,
  });
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const address = useQuery({
    queryKey: ['billing', 'address'],
    queryFn: () => unwrap(api.GET('/billing/address')),
  });
  if (services.isPending || billing.isPending || address.isPending) return <Loading />;
  if (services.isError || billing.isError || address.isError) {
    const error = services.error ?? billing.error ?? address.error;
    return (
      <LoadError
        message={errorMessage(error, t)}
        onRetry={() => void Promise.all([services.refetch(), billing.refetch(), address.refetch()])}
      />
    );
  }
  const service = services.data.items.find((s) => s.code === serviceCode);
  const help = siteId
    ? `/sites/${siteId}/help?service=${serviceCode}${findingId ? `&finding=${findingId}` : ''}`
    : '/requests';
  return (
    <>
      <PageTitle>{t('order.title')}</PageTitle>
      <div className="flex flex-col gap-4">
        <BackButton to={siteId ? `/sites/${siteId}` : '/requests'}>{t('order.back')}</BackButton>
        {!service?.orderable ? (
          <Notice kind="info">
            {t('order.notOrderable')}{' '}
            <LinkButton to={help} variant="soft" size="sm">
              {t('order.askInstead')}
            </LinkButton>
          </Notice>
        ) : (
          <>
            {!billing.data.live ? <Notice kind="warning">{t('checkout.demo')}</Notice> : null}
            <p className="max-w-prose text-slate-800">{t('order.intro')}</p>
            <OrderForm
              initial={address.data.address}
              countries={address.data.countries}
              product={{ kind: 'service', service, siteId, findingId }}
              methods={billing.data.methods}
            />
          </>
        )}
      </div>
    </>
  );
}
